import { type Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { findLinkAnchorFromEvent, getCaretLinkAnchor, selectLink } from './link-anchor';

/**
 * Bridges the delay between the pointer leaving the link and entering the
 * (offset) card — a straight mouseout would flicker the card closed across the
 * gap, so a leave is debounced and cancelled if the pointer arrives on the card.
 */
const HOVER_CLOSE_DELAY_MS = 400;

const editorsEditingLinks = new WeakSet<Editor>();

/** Keeps the sibling text toolbar hidden while this editor's link form is open. */
export const isLinkMenuEditing = (editor: Editor): boolean =>
  editorsEditingLinks.has(editor);

export interface LinkMenu {
  /** The link the card describes, or null when closed. */
  anchor: HTMLAnchorElement | null;
  /** Virtual element handed to `PopoverAnchor`, tracking the live link rect. */
  anchorRef: React.RefObject<{ getBoundingClientRect: () => DOMRect }>;
  /** Whether the card is showing the edit form rather than the actions. */
  isEditing: boolean;
  /** Radix `onOpenChange`: outside-click / Escape close the card. */
  setOpen: (open: boolean) => void;
  /** Retarget the hovered link and switch the card to the edit form. */
  startEditing: () => void;
  /** Retarget the hovered link and unset its mark. */
  removeLink: () => void;
  /** Keep the card open while the pointer is over it. */
  onCardMouseEnter: () => void;
  /** Start the debounced close when the pointer leaves the card. */
  onCardMouseLeave: () => void;
}

/**
 * Detects a hovered link (pointer) or a caret resting in a link (keyboard) and
 * exposes the state a controlled link `Popover` needs. The card itself is a
 * sibling React component anchored via `anchorRef`, so — unlike v1's HoverLink —
 * no ProseMirror decoration or `ReactRenderer` machinery is involved.
 */
export const useLinkMenu = (editor: Editor): LinkMenu => {
  const [anchor, setAnchor] = useState<HTMLAnchorElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // `| null` in the type param yields a mutable ref; `useRef<T>(null)` would be
  // a read-only RefObject and reject the reassignment below.
  const anchorRef = useRef<{ getBoundingClientRect: () => DOMRect } | null>(null);
  const editingRef = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const reset = useCallback(() => {
    clearTimeout(closeTimer.current);
    editorsEditingLinks.delete(editor);
    editingRef.current = false;
    setIsEditing(false);
    setAnchor(null);
  }, [editor]);

  const show = useCallback((next: HTMLAnchorElement) => {
    // Pinned while editing: hovering or clicking into another link must not
    // repoint the open edit card at a different link.
    if (editingRef.current) {
      return;
    }
    clearTimeout(closeTimer.current);
    anchorRef.current = { getBoundingClientRect: () => next.getBoundingClientRect() };
    setAnchor(next);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      if (editingRef.current) {
        return;
      }

      const caretAnchor = getCaretLinkAnchor(editor);
      if (caretAnchor) {
        show(caretAnchor);
      } else {
        setAnchor(null);
      }
    }, HOVER_CLOSE_DELAY_MS);
  }, [editor, show]);

  useEffect(() => {
    const dom = editor.view.dom;

    const onOver = (event: MouseEvent) => {
      const linkAnchor = findLinkAnchorFromEvent(event.target);
      if (linkAnchor) {
        show(linkAnchor);
      }
    };
    const onOut = (event: MouseEvent) => {
      if (findLinkAnchorFromEvent(event.target)) {
        scheduleClose();
      }
    };

    dom.addEventListener('mouseover', onOver);
    dom.addEventListener('mouseout', onOut);
    return () => {
      dom.removeEventListener('mouseover', onOver);
      dom.removeEventListener('mouseout', onOut);
      clearTimeout(closeTimer.current);
      editorsEditingLinks.delete(editor);
    };
  }, [editor, show, scheduleClose]);

  useEffect(() => {
    const onSelectionUpdate = () => {
      const caretAnchor = getCaretLinkAnchor(editor);
      if (caretAnchor) {
        show(caretAnchor);
      } else if (!editingRef.current) {
        setAnchor(null);
      }
    };
    editor.on('selectionUpdate', onSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', onSelectionUpdate);
    };
  }, [editor, show]);

  // A live `setContent` (streaming, autosave echo, chat Add/Replace) can replace
  // the doc while the card is open — even unfocused, so no mouseout fires. The
  // hovered `<a>` is then detached: its rect collapses to 0,0 and its position
  // is gone, so close rather than let the card linger over a stale node.
  useEffect(() => {
    if (!anchor) {
      return;
    }
    const onUpdate = () => {
      if (!editor.view.dom.contains(anchor)) {
        reset();
      }
    };
    editor.on('update', onUpdate);
    return () => {
      editor.off('update', onUpdate);
    };
  }, [editor, anchor, reset]);

  const setOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        reset();
      }
    },
    [reset]
  );

  // Retarget the hovered link, then act. `selectLink` moves the selection to a
  // non-empty range, which fires `selectionUpdate` synchronously — so the pin
  // (`editingRef`) must be set *before* `selectLink` runs, or that listener
  // would see no caret-in-link and close the card out from under the edit form.
  const startEditing = useCallback(() => {
    if (!anchor) {
      reset();
      return;
    }
    editorsEditingLinks.add(editor);
    editingRef.current = true;
    setIsEditing(true);
    if (!selectLink(editor, anchor)) {
      reset();
    }
  }, [editor, anchor, reset]);

  // `selectLink` fails when the anchor has detached (a live setContent replaced
  // the doc); bail rather than unset a mark at the current selection.
  const removeLink = useCallback(() => {
    if (anchor && selectLink(editor, anchor)) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    reset();
  }, [editor, anchor, reset]);

  return {
    anchor,
    anchorRef,
    isEditing,
    setOpen,
    startEditing,
    removeLink,
    onCardMouseEnter: () => clearTimeout(closeTimer.current),
    onCardMouseLeave: scheduleClose,
  };
};
